import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { Mark } from "../../lib/Mark.tsx";

const nav = [
  { to: "/download", label: "Download" },
  { to: "/transparency", label: "Transparency" },
  { to: "/account", label: "Account" },
];

/** How far the page is scrolled, rounded to what the header and the
 *  back-to-top button need: past the top, and past a screen and a half. */
function useScrolled() {
  const [at, setAt] = useState({ past: false, far: false });
  useEffect(() => {
    const read = () => {
      const y = window.scrollY;
      setAt((was) => {
        const now = { past: y > 8, far: y > window.innerHeight * 1.5 };
        return now.past === was.past && now.far === was.far ? was : now;
      });
    };
    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, []);
  return at;
}

function toTop() {
  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: still ? "auto" : "smooth" });
  document.querySelector<HTMLElement>("#top")?.focus({ preventScroll: true });
}

export function PublicLayout() {
  const { past, far } = useScrolled();
  const { pathname } = useLocation();
  // a new page starts at its top, as a link to another site would. The braces
  // matter: newer browsers return a promise from scrollTo, and React would call
  // anything returned here as the effect's cleanup, and crash on the next page.
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="flex min-h-dvh flex-col">
      <header className={`site-header sticky top-0 z-30 ${past ? "site-header-scrolled" : ""}`}>
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-between gap-2 px-4 py-3.5 sm:gap-3">
          <Link to="/" id="top" tabIndex={-1} className="on-sky flex items-center gap-2.5 font-display text-lg font-medium text-ink no-underline">
            <Mark size={34} pulse />
            {/* a phone has room for the mark and the three links on one row, not the name too */}
            <span className="max-sm:sr-only">Atzmai OS</span>
          </Link>
          <nav className="flex sm:gap-1" aria-label="Main">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) => `rounded-full px-2 py-1.5 font-display text-[13px] font-medium sm:px-3.5 sm:text-sm no-underline ${isActive ? "bg-ink text-orbit" : "on-sky text-ink hover:bg-white/10"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1100px] flex-1 px-4 pt-6 pb-24">
        <Outlet />
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-[1100px] flex-col gap-6 px-4 py-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <Mark size={28} />
              <span className="font-display font-medium text-ink">Atzmai OS</span>
              <span className="text-[15px] text-ink-3">built on Debian 13</span>
            </div>
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-[15px]" aria-label="Footer">
              <Link to="/download">Download</Link>
              <Link to="/transparency">What we collect</Link>
              <Link to="/account">Your account</Link>
            </nav>
          </div>
          <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 text-[14px] text-ink-3">
            <span>© {new Date().getFullYear()} Ebed IT Solutions Limited. All rights reserved.</span>
            <span>
              Developed by <a href="https://ebeditsolutions.com" target="_blank" rel="noreferrer">Ebed IT Solutions Limited</a>
            </span>
          </div>
        </div>
      </footer>

      <button type="button" onClick={toTop} className={`to-top ${far ? "to-top-shown" : ""}`} aria-label="Back to the top" tabIndex={far ? 0 : -1}>
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M10 15V5M5 10l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    </div>
  );
}
